/**
 * Real-time streaming service for backend step updates.
 * Receives server-sent events (SSE) with structured step progress.
 */
import { BACKEND_URL } from "./pipelineService";

export type StepEventType = 'update' | 'success' | 'error' | 'message';

export type StepEvent = {
  type: StepEventType;
  step: string;
  text: string;
  progress?: {
    current: number;
    total: number;
  };
  timestamp?: string;
};

export type StepEventCallback = (event: StepEvent) => void;

let eventSource: EventSource | null = null;
let shouldStopRealtime = false;
let reconnectTimer: number | null = null;
let connectionTimeoutTimer: number | null = null;
const CONNECTION_TIMEOUT_MS = 10000; // 10 second timeout to detect stuck connections

/**
 * Start listening for real-time step events from the backend.
 * Backend should stream Server-Sent Events (SSE) with JSON step updates.
 * 
 * Expected SSE format:
 * event: message
 * data: {"type":"update","step":"summarise_pages","text":"Summarising pages (3/12)","progress": null}
 * 
 * @param documentId - Document session ID to stream events for
 * @param callback - Function called for each step event received
 * @param backendUrl - Base URL of backend (default from env)
 */
export function startRealtimeUpdates(
  documentId: string,
  callback: StepEventCallback,
  backendUrl?: string
): Promise<void> {
  // Ensure any previous run is cleared
  stopRealtimeUpdates();
  shouldStopRealtime = false;

  const configured = backendUrl || BACKEND_URL;
  if (!configured || configured === "undefined") {
    console.error("realtimeService: No backend URL configured");
    return Promise.resolve();
  }

  return new Promise((resolve) => {

  // Helper to build the SSE URL for the new /events/{session_id} endpoint
  function buildSseUrl(): string {
    try {
      let base = configured;
      if (base.includes("/run_pipeline")) {
        base = base.split("/run_pipeline")[0];
      }
      const u = new URL(base);
      u.pathname = `/events/${documentId}`;
      return u.toString();
    } catch (err) {
      // Fallback: treat as relative path
      let base = configured.replace(/\/run_pipeline.*$/, "");
      return `${base}/events/${encodeURIComponent(documentId)}`;
    }
  }

  console.log("realtimeService: Starting real-time updates for document", documentId);

  let retryCount = 0;
  const maxRetries = 8;
  const baseDelayMs = 500; // initial backoff
  let resolved = false;

  function clearConnectionTimeout() {
    if (connectionTimeoutTimer) {
      clearTimeout(connectionTimeoutTimer);
      connectionTimeoutTimer = null;
    }
  }

  function scheduleConnectionTimeout() {
    clearConnectionTimeout();
    connectionTimeoutTimer = window.setTimeout(() => {
      console.warn("realtimeService: Connection timeout after 10s, closing and retrying");
      if (eventSource) {
        eventSource.close();
        eventSource = null;
      }
      
      if (!resolved) {
        resolved = true;
        resolve();
      }

      if (retryCount < maxRetries) {
        scheduleReconnect();
      } else {
        console.warn("realtimeService: Max reconnect attempts reached after timeout");
      }
    }, CONNECTION_TIMEOUT_MS);
  }

  function scheduleReconnect() {
    if (shouldStopRealtime) return;
    clearConnectionTimeout();
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    const delay = Math.min(30000, baseDelayMs * Math.pow(2, retryCount));
    console.log(`realtimeService: Scheduling reconnect in ${delay}ms (attempt ${retryCount + 1})`);
    reconnectTimer = window.setTimeout(() => {
      retryCount++;
      connect();
    }, delay);
  }

  function connect() {
    if (shouldStopRealtime) return;
    const sseUrl = buildSseUrl();

    try {
      eventSource = new EventSource(sseUrl);
      scheduleConnectionTimeout(); // Start timeout when connecting

      eventSource.addEventListener("open", () => {
        console.log("realtimeService: Stream connection opened successfully");
        clearConnectionTimeout(); // Clear timeout once connected
        retryCount = 0;
        if (!resolved) {
          resolved = true;
          resolve();
        }
      });
eventSource.addEventListener("message", (e: Event) => {

        const messageEvent = e as MessageEvent;
        try {
          const stepEvent: StepEvent = JSON.parse(messageEvent.data);
          console.log("realtimeService: Received step event", stepEvent);
          callback(stepEvent);

          // Close stream only on error or explicit completion message
          if (stepEvent.type === "error" || (stepEvent.type === "message" && stepEvent.text === "Analysis completed")) {
            console.log("realtimeService: Received completion event, closing stream");
            stopRealtimeUpdates();
          }
        } catch (err) {
          console.error("realtimeService: Failed to parse step event", err, messageEvent.data);
        }
      });
      eventSource.addEventListener("error", (e: Event) => {
        // Get more details about the error
        const readyState = eventSource?.readyState;
        const readyStateStr = readyState === EventSource.CONNECTING ? "CONNECTING" : 
                             readyState === EventSource.OPEN ? "OPEN" : 
                             readyState === EventSource.CLOSED ? "CLOSED" : "UNKNOWN";
        
        console.error(
          `realtimeService: EventSource error (readyState=${readyStateStr}, attempt ${retryCount + 1}/${maxRetries})`,
          e
        );

        clearConnectionTimeout();

        if (!resolved) {
          resolved = true;
          resolve();
        }

        // If the connection was closed by the server, attempt reconnect with backoff.
        if (eventSource?.readyState === EventSource.CLOSED) {
          console.log("realtimeService: Stream closed by server");
          eventSource.close();
          eventSource = null;
          if (retryCount < maxRetries) {
            scheduleReconnect();
          } else {
            console.warn("realtimeService: Max reconnect attempts reached");
          }
        } else if (eventSource?.readyState === EventSource.CONNECTING) {
          // Connection is still trying; schedule a reconnect to break the stuck state
          console.log("realtimeService: Connection stuck in CONNECTING state, will retry");
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }
          if (retryCount < maxRetries) {
            scheduleReconnect();
          }
        } else {
          // Non-fatal error or OPEN state error; schedule a reconnect to recover transient issues
          console.log("realtimeService: Transient error, scheduling reconnect");
          if (retryCount < maxRetries) {
            scheduleReconnect();
          } else {
            console.warn("realtimeService: Max reconnect attempts reached");
          }
        }
      });
    } catch (err) {
      console.error("realtimeService: Failed to create EventSource", err);
      if (!resolved) {
        resolved = true;
        resolve();
      }
      if (retryCount < maxRetries) scheduleReconnect();
    }
  }

  connect();
  });
}

/**
 * Stop listening for real-time updates.
 */
export function stopRealtimeUpdates(): void {
  shouldStopRealtime = true;
  
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  
  if (connectionTimeoutTimer) {
    clearTimeout(connectionTimeoutTimer);
    connectionTimeoutTimer = null;
  }
  
  console.log("realtimeService: Stream connection closed and all timers cleared");
}

/**
 * Check if real-time stream is currently active.
 */
export function isRealtimeActive(): boolean {
  return eventSource !== null && eventSource.readyState === EventSource.OPEN;
}